/**
 * Private docs API resource
 * Endpoint: /api/easyblocks/private-docs
 */

import { getApiBaseUrl, joinUrl } from "./client";
import type {
  PrivateDocCategoriesParams,
  PrivateDocCategoriesResponse,
  PrivateDocListParams,
  PrivateDocListResponse,
  PrivateDocUploadParams,
  PrivateDocUploadResponse,
} from "./types";

/**
 * GET /api/easyblocks/private-docs?limit=&offset=&category_id=&year=
 * List private documents
 */
export async function listPrivateDocs(params: PrivateDocListParams): Promise<PrivateDocListResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "private-docs"), window.location.origin);
  if (params.limit !== undefined) url.searchParams.set("limit", String(params.limit));
  if (params.offset !== undefined) url.searchParams.set("offset", String(params.offset));
  if (params.category_id) url.searchParams.set("category_id", String(params.category_id));
  if (params.year) url.searchParams.set("year", String(params.year));
  if (params.document_scope) {
    url.searchParams.set("document_scope", params.document_scope);
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Private docs list failed (${res.status} ${res.statusText}): ${text}`);
  }

  return (await res.json()) as PrivateDocListResponse;
}

/**
 * GET /api/easyblocks/private-docs?action=categories
 * List available categories
 */
export async function listPrivateDocCategories(
  params: PrivateDocCategoriesParams = {}
): Promise<PrivateDocCategoriesResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "private-docs"), window.location.origin);
  url.searchParams.set("action", "categories");
  if (params.document_scope) {
    url.searchParams.set("document_scope", params.document_scope);
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Private docs categories failed (${res.status} ${res.statusText}): ${text}`);
  }

  return (await res.json()) as PrivateDocCategoriesResponse;
}

/**
 * POST /api/easyblocks/private-docs
 * Upload a private document
 */
export async function uploadPrivateDoc(params: PrivateDocUploadParams): Promise<PrivateDocUploadResponse> {
  const fd = new FormData();
  fd.append("file", params.file);
  if (params.category_id) fd.append("category_id", String(params.category_id));
  if (params.item_name) fd.append("item_name", params.item_name);

  const res = await fetch(joinUrl(getApiBaseUrl(), "private-docs"), {
    method: "POST",
    body: fd,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Private docs upload failed (${res.status} ${res.statusText}): ${text}`);
  }

  return (await res.json()) as PrivateDocUploadResponse;
}
